// Client-side helpers for UX29 Google OAuth sign-in.
//
// Companion to api/google-oauth-callback.js. The server function
// receives Google's auth-code callback, exchanges it for tokens,
// derives the encryption key, and redirects the user back to the SPA
// with the result packed in the URL hash fragment. This module runs on
// return:
//   1. parseOAuthCallbackHash() — decode the hash
//   2. resolveNostrIdentity()   — list / fetch / decrypt the Drive
//                                 appData identity file, or create a
//                                 new one on first sign-in
//
// Encryption format for the stored identity file:
//   bytes[0..12]  = random IV (12 bytes, AES-GCM standard)
//   bytes[12..]   = AES-GCM(encKey, iv, utf8(nsec)) [ciphertext + tag]
//
// The 32-byte encKey comes from the server-side HKDF derivation; it
// never leaves this device after we read it from the hash fragment.
// The access token likewise lives only in memory for the duration of
// the Drive API calls.

const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth'
const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3'
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3'
const IDENTITY_FILE_NAME = 'verdant-identity.enc'
const PREFS_FILE_NAME = 'verdant-prefs.json'
const SS_DRIVE_TOKEN = 'verdant_drive_token'
const STATE_COOKIE = 'google_oauth_state'
const STATE_COOKIE_MAX_AGE_SECONDS = 600

// --------------------------- OAuth flow start ---------------------------

function randomStateToken() {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return bytesToBase64Url(bytes)
}

function setStateCookie(value) {
  const secure = window.location.protocol === 'https:' ? 'Secure; ' : ''
  // HttpOnly is not possible when the client sets the cookie — this is
  // the standard tradeoff for CSRF cookies in a serverless setup. The
  // SameSite=Lax ensures the cookie accompanies the top-level
  // navigation back from Google. Short Max-Age bounds the window a
  // stolen cookie could be used.
  document.cookie =
    `${STATE_COOKIE}=${value}; Path=/; Max-Age=${STATE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; ${secure}`
}

export function startGoogleOAuth() {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID
  if (!clientId) {
    throw new Error('Google OAuth not configured')
  }
  const state = randomStateToken()
  setStateCookie(state)

  const redirectUri = `${window.location.origin}/api/google-oauth-callback`
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid profile https://www.googleapis.com/auth/drive.appdata',
    state,
    // Pre-select a Google account each time rather than silently using
    // whichever one is cached; mainstream users may have multiple logins.
    prompt: 'select_account',
    // We don't need offline access in Phase 1; the client re-triggers
    // OAuth when the Drive token expires rather than exchanging a
    // refresh token.
    access_type: 'online',
    include_granted_scopes: 'true',
  })
  window.location.href = `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`
}

// --------------------- OAuth callback hash parsing ---------------------

// Called on every page load. If the URL hash carries a Google OAuth
// result, returns it and clears the hash via history.replaceState so a
// screen share / history entry / third-party extension doesn't see the
// token. Returns null if the hash isn't an OAuth result.
export function parseOAuthCallbackHash() {
  const raw = window.location.hash || ''
  if (!raw.startsWith('#')) return null

  const params = new URLSearchParams(raw.slice(1))
  const errorReason = params.get('google_auth_error')
  const payload = params.get('google_auth')

  if (!errorReason && !payload) return null

  // Clear the hash immediately. The URL path + query are preserved.
  const cleanUrl = window.location.pathname + window.location.search
  window.history.replaceState(null, '', cleanUrl)

  if (errorReason) {
    // The server-side regex restricts reason codes to [a-z_]{1,32}, so
    // this is safe to show in UI without further sanitization. Still,
    // we only accept the known set.
    const KNOWN_REASONS = new Set([
      'config_missing', 'google_denied', 'missing_params',
      'state_mismatch', 'exchange_failed', 'malformed_response',
      'kdf_failed', 'unknown',
    ])
    const reason = KNOWN_REASONS.has(errorReason) ? errorReason : 'unknown'
    return { ok: false, reason }
  }

  try {
    const json = base64UrlDecodeToString(payload)
    const parsed = JSON.parse(json)
    const enc = parsed?.enc
    const drv = parsed?.drv
    const exp = parsed?.exp
    if (typeof enc !== 'string' || typeof drv !== 'string' || typeof exp !== 'number') {
      return { ok: false, reason: 'malformed_response' }
    }
    return { ok: true, enc, drv, exp }
  } catch {
    return { ok: false, reason: 'malformed_response' }
  }
}

// -------------------------- Drive appData I/O --------------------------

async function driveFetch(url, accessToken, init = {}) {
  const resp = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return resp
}

async function findIdentityFileId(accessToken) {
  return findFileIdByName(accessToken, IDENTITY_FILE_NAME)
}

async function findFileIdByName(accessToken, name) {
  const url = new URL(`${DRIVE_API_BASE}/files`)
  url.searchParams.set('spaces', 'appDataFolder')
  url.searchParams.set('q', `name='${name}' and trashed=false`)
  url.searchParams.set('fields', 'files(id,name)')
  const resp = await driveFetch(url.toString(), accessToken)
  if (!resp.ok) {
    throw new Error(`Drive list failed: ${resp.status}`)
  }
  const data = await resp.json()
  const file = data?.files?.[0]
  return file?.id || null
}

async function readIdentityFile(accessToken, fileId) {
  const url = `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media`
  const resp = await driveFetch(url, accessToken)
  if (!resp.ok) {
    throw new Error(`Drive read failed: ${resp.status}`)
  }
  const buf = await resp.arrayBuffer()
  return new Uint8Array(buf)
}

async function createIdentityFile(accessToken, ciphertextBytes) {
  // Multipart upload — Drive requires metadata + content separated by a
  // random boundary. The identity file lives in appDataFolder, which is
  // scoped to this OAuth app only.
  const boundary = `verdant-boundary-${randomStateToken().slice(0, 16)}`
  const metadata = JSON.stringify({
    name: IDENTITY_FILE_NAME,
    parents: ['appDataFolder'],
  })
  const encoder = new TextEncoder()
  const parts = [
    encoder.encode(`--${boundary}\r\n`),
    encoder.encode('Content-Type: application/json; charset=UTF-8\r\n\r\n'),
    encoder.encode(metadata),
    encoder.encode(`\r\n--${boundary}\r\n`),
    encoder.encode('Content-Type: application/octet-stream\r\n\r\n'),
    ciphertextBytes,
    encoder.encode(`\r\n--${boundary}--`),
  ]
  const body = concatBytes(parts)

  const url = `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart`
  const resp = await driveFetch(url, accessToken, {
    method: 'POST',
    headers: {
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body,
  })
  if (!resp.ok) {
    throw new Error(`Drive create failed: ${resp.status}`)
  }
  return resp.json()
}

// ------------------------- AES-GCM encryption -------------------------

async function importEncKey(encKeyB64url) {
  const raw = base64UrlDecodeToBytes(encKeyB64url)
  if (raw.length !== 32) throw new Error('Invalid encryption key length')
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function encryptNsec(nsec, encKeyB64url) {
  const key = await importEncKey(encKeyB64url)
  const iv = new Uint8Array(12)
  crypto.getRandomValues(iv)
  const plaintext = new TextEncoder().encode(nsec)
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext),
  )
  // Prepend IV so decrypt can recover it.
  const out = new Uint8Array(iv.length + ciphertext.length)
  out.set(iv, 0)
  out.set(ciphertext, iv.length)
  return out
}

async function decryptNsec(bytes, encKeyB64url) {
  if (bytes.length < 13) throw new Error('ciphertext too short')
  const key = await importEncKey(encKeyB64url)
  const iv = bytes.slice(0, 12)
  const ciphertext = bytes.slice(12)
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext,
  )
  return new TextDecoder().decode(plaintext)
}

// ----------------------- High-level orchestration -----------------------

// Given a parsed OAuth callback and a nsec factory (used only on first
// sign-in), returns the nsec to sign the user in with.
//
// On first sign-in for this Google account: generateNsec() is called,
// the result is encrypted, uploaded to Drive, and returned.
// On subsequent sign-ins: the existing file is fetched + decrypted.
export async function resolveNostrIdentity({ enc, drv }, generateNsec) {
  const existingId = await findIdentityFileId(drv)
  if (existingId) {
    const ciphertext = await readIdentityFile(drv, existingId)
    return decryptNsec(ciphertext, enc)
  }
  // First sign-in: generate, encrypt, upload.
  const nsec = generateNsec()
  const ciphertext = await encryptNsec(nsec, enc)
  await createIdentityFile(drv, ciphertext)
  return nsec
}

// UX29 Phase 2c linking helpers: read the Drive-stored identity (if
// any) so the caller can compare pubkeys before overwriting, and
// write the caller's current nsec whether or not a file exists.

export async function readLinkedDriveFile({ drv, enc }) {
  const fileId = await findIdentityFileId(drv)
  if (!fileId) return { fileId: null, existingNsec: null }
  const ciphertext = await readIdentityFile(drv, fileId)
  const existingNsec = await decryptNsec(ciphertext, enc)
  return { fileId, existingNsec }
}

export async function writeLinkedDriveFile({ drv, enc, nsec, existingFileId }) {
  const ciphertext = await encryptNsec(nsec, enc)
  if (existingFileId) {
    await updateIdentityFile(drv, existingFileId, ciphertext)
    return existingFileId
  }
  const created = await createIdentityFile(drv, ciphertext)
  return created?.id
}

async function updateIdentityFile(accessToken, fileId, ciphertextBytes) {
  const url =
    `${DRIVE_UPLOAD_BASE}/files/${encodeURIComponent(fileId)}` +
    `?uploadType=media`
  const resp = await driveFetch(url, accessToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: ciphertextBytes,
  })
  if (!resp.ok) throw new Error(`Drive update failed: ${resp.status}`)
  return resp.json()
}

// UX44: user-preference sync. Separate from the identity file so we
// can read/write it without touching the sensitive key material.
// Content is a plain JSON object (currently just { jurisdiction }).
// NOT encrypted — the jurisdiction tag is already on every vote
// event the user publishes, so there's no secret to protect.

export async function readPrefsFile({ drv }) {
  try {
    const fileId = await findFileIdByName(drv, PREFS_FILE_NAME)
    if (!fileId) return null
    const url = `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media`
    const resp = await driveFetch(url, drv)
    if (!resp.ok) return null
    const text = await resp.text()
    try { return JSON.parse(text) } catch { return null }
  } catch {
    return null
  }
}

export async function writePrefsFile({ drv, prefs }) {
  const body = JSON.stringify(prefs || {})
  const fileId = await findFileIdByName(drv, PREFS_FILE_NAME)
  if (fileId) {
    const url =
      `${DRIVE_UPLOAD_BASE}/files/${encodeURIComponent(fileId)}` +
      `?uploadType=media`
    const resp = await driveFetch(url, drv, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
    if (!resp.ok) throw new Error(`Drive prefs update failed: ${resp.status}`)
    return resp.json()
  }
  const boundary = `verdant-boundary-${randomStateToken().slice(0, 16)}`
  const metadata = JSON.stringify({
    name: PREFS_FILE_NAME,
    parents: ['appDataFolder'],
  })
  const encoder = new TextEncoder()
  const parts = [
    encoder.encode(`--${boundary}\r\n`),
    encoder.encode('Content-Type: application/json; charset=UTF-8\r\n\r\n'),
    encoder.encode(metadata),
    encoder.encode(`\r\n--${boundary}\r\n`),
    encoder.encode('Content-Type: application/json\r\n\r\n'),
    encoder.encode(body),
    encoder.encode(`\r\n--${boundary}--`),
  ]
  const multipart = concatBytes(parts)
  const url = `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart`
  const resp = await driveFetch(url, drv, {
    method: 'POST',
    headers: {
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body: multipart,
  })
  if (!resp.ok) throw new Error(`Drive prefs create failed: ${resp.status}`)
  return resp.json()
}

// UX44: cache the Drive access token in sessionStorage so prefs writes
// after sign-in don't require a re-OAuth. Expires in 1h per Google's
// default access_token lifetime; we check the exp claim before using.
export function persistDriveToken(drv, exp) {
  if (typeof sessionStorage === 'undefined' || !drv) return
  try {
    sessionStorage.setItem(SS_DRIVE_TOKEN, JSON.stringify({
      drv,
      exp: Number(exp) || 0,
    }))
  } catch { /* noop */ }
}

export function loadDriveToken() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SS_DRIVE_TOKEN)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.drv !== 'string') return null
    if (Number(parsed.exp) <= Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(SS_DRIVE_TOKEN)
      return null
    }
    return parsed.drv
  } catch { return null }
}

export function clearDriveToken() {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.removeItem(SS_DRIVE_TOKEN) } catch { /* noop */ }
}

// ----------------------------- utilities -----------------------------

function bytesToBase64Url(bytes) {
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlDecodeToString(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
  return atob(b64 + pad)
}

function base64UrlDecodeToBytes(b64url) {
  const s = base64UrlDecodeToString(b64url)
  const bytes = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i)
  return bytes
}

function concatBytes(parts) {
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let offset = 0
  for (const p of parts) {
    out.set(p, offset)
    offset += p.length
  }
  return out
}
